class CartRecommendations {
  constructor() {
    this.cartRecommendationsContainer = null;
    this.cartItemsContainer = null;
    this.originalRecommendations = [];
    this.originalRecommendationProductIds = new Set();
    this.dynamicRecommendations = [];
    this.currentCartProductIds = new Set();
    this.previousCartProductIds = new Set();
    this.removedProductsData = new Map();
    this.addedFromRecommendations = new Set();
    this.hasRestrictedItems = false;
    this.isInitialized = false;
    this.isCartDrawerOpen = false;
    this.variantToProductMap = new Map();
    
    this.setupCartDrawerListeners();
  }

  setupCartDrawerListeners() {
    document.addEventListener('cart:open', () => {
      this.onCartDrawerOpen();
    });

    document.addEventListener('cart:toggle', () => {
      this.checkCartDrawerState();
    });

    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-drawer-toggle], .cart-toggle, .cart-drawer-toggle, [href="#cart-drawer"]')) {
        setTimeout(() => this.checkCartDrawerState(), 100);
      }
    });

    this.setupCartDrawerObserver();
  }

  setupCartDrawerObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.querySelector && node.querySelector('.cart-recommendations')) {
                this.onCartDrawerOpen();
              }
              else if (node.matches && (node.matches('.cart-drawer') || node.matches('[data-cart-drawer]'))) {
                setTimeout(() => this.checkCartDrawerState(), 100);
              }
            }
          });
        }
        
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.matches('.cart-drawer, [data-cart-drawer]')) {
            this.checkCartDrawerState();
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'aria-hidden', 'data-cart-drawer']
    });
  }

  checkCartDrawerState() {
    const cartDrawer = document.querySelector('.cart-drawer, #cart-drawer, [data-cart-drawer]');
    if (!cartDrawer) {
      return;
    }

    const isOpen = this.isCartDrawerOpenNow(cartDrawer);
    
    if (isOpen && !this.isCartDrawerOpen) {
      this.onCartDrawerOpen();
    } else if (!isOpen && this.isCartDrawerOpen) {
      this.isCartDrawerOpen = false;
    }
  }

  isCartDrawerOpenNow(cartDrawer) {
    return (
      cartDrawer.classList.contains('open') || 
      cartDrawer.classList.contains('active') || 
      cartDrawer.classList.contains('is-open') ||
      cartDrawer.classList.contains('cart-drawer--open') ||
      cartDrawer.getAttribute('aria-hidden') === 'false' ||
      (getComputedStyle(cartDrawer).display !== 'none' && 
       getComputedStyle(cartDrawer).visibility !== 'hidden' &&
       getComputedStyle(cartDrawer).opacity !== '0')
    );
  }

  onCartDrawerOpen() {
    this.isCartDrawerOpen = true;
    
    setTimeout(() => {
      this.initialize();
    }, 150);
  }

  async initialize() {
    this.cartRecommendationsContainer = document.querySelector('.cart-recommendations');
    this.cartItemsContainer = document.querySelector('#cartItemsHolder, .cart-drawer__items, .cart__items, [data-items-holder]');
    
    if (!this.cartRecommendationsContainer) {
      return;
    }

    if (this.isInitialized) {
      await this.refresh();
      return;
    }

    this.storeOriginalRecommendations();
    await this.updateCurrentCartProductIds();
    this.setupCartObserver();
    this.setupCartEventListeners();
    this.filterRecommendations();
    this.isInitialized = true;
  }

  storeOriginalRecommendations() {
    const recommendationProducts = this.cartRecommendationsContainer.querySelectorAll('.cart-recommendation-product');
    
    this.originalRecommendations = Array.from(recommendationProducts).map((product, index) => {
      const form = product.querySelector('form[data-product-form]');
      const productIdInput = form ? form.querySelector('input[name="product-id"]') : null;
      const productId = productIdInput ? productIdInput.value : null;
      
      const dataProductId = product.getAttribute('data-product-id');
      const isInCartAtRender = product.getAttribute('data-in-cart') === 'true';
      
      if (productId || dataProductId) {
        const finalProductId = productId || dataProductId;
        this.originalRecommendationProductIds.add(finalProductId);
      }
      
      return {
        element: product.cloneNode(true),
        productId: productId || dataProductId,
        wasInCartAtRender: isInCartAtRender
      };
    });
  }

  async updateCurrentCartProductIds() {
    this.previousCartProductIds = new Set(this.currentCartProductIds);
    this.currentCartProductIds.clear();
    
    this.hasRestrictedItems = false;

    // Simple approach: Use Shopify's cart API directly
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cartData = await response.json();
        
        if (cartData.items && cartData.items.length > 0) {
          cartData.items.forEach((item, index) => {
            // Add both product ID and variant ID to our set
            if (item.product_id) {
              this.currentCartProductIds.add(item.product_id.toString());
            }
            if (item.variant_id) {
              this.currentCartProductIds.add(item.variant_id.toString());
            }
          });
        }
      }
    } catch (error) {
      // Silently handle cart API errors
    }

    // Also process DOM cart items for recently removed feature data
    const cartItems = this.cartItemsContainer ? 
      this.cartItemsContainer.querySelectorAll('.cart__item[data-cart-item], .cart__item[data-item]') : 
      document.querySelectorAll('#cartItemsHolder .cart__item[data-cart-item], #cartItemsHolder .cart__item[data-item]');

    cartItems.forEach((item, index) => {
      const dataItem = item.getAttribute('data-item') || item.getAttribute('data-cart-item');
      if (dataItem) {
        const variantId = dataItem.split(':')[0];
        if (variantId) {
          // Store cart item data for recently removed feature
          this.storeCartItemData(item, variantId);
          this.checkCartItemForRestrictedTags(item, index + 1);
        }
      }
    });

    await this.handleRemovedItems();
  }

  storeCartItemData(cartItemElement, productId) {
    try {
      const titleElement = cartItemElement.querySelector('.cart__item__title a, .cart__item__title, .cart-item__title, .cart__item-name, .product-title, [data-cart-item-title]');
      const title = titleElement ? titleElement.textContent.trim() : 'Unknown Product';
      
      const imageElement = cartItemElement.querySelector('img');
      let imageUrl = '';
      if (imageElement) {
        imageUrl = imageElement.src || imageElement.getAttribute('data-src') || '';
      }
      
      const priceElement = cartItemElement.querySelector('.cart__item__price, .cart-item__price, .price, [data-cart-item-price]');
      let price = 0;
      if (priceElement) {
        const priceText = priceElement.textContent.trim();
        // Look for price patterns more thoroughly
        const priceMatch = priceText.match(/\$?(\d+(?:[,\.]\d{3})*(?:\.\d{2})?)/);
        if (priceMatch) {
          // Remove commas and convert to number
          const cleanPrice = priceMatch[1].replace(/,/g, '');
          const parsedPrice = parseFloat(cleanPrice);
          
          if (!isNaN(parsedPrice)) {
            // If it's less than 100, it's likely in dollars, convert to cents
            if (parsedPrice < 100) {
              price = Math.round(parsedPrice * 100);
            } else {
              // Already in cents
              price = Math.round(parsedPrice);
            }
          }
        }
      }
      
      const variantIdInput = cartItemElement.querySelector('input[name="id"], input[data-variant-id]');
      const variantId = variantIdInput ? variantIdInput.value : productId;
      
      const productData = {
        id: productId,
        title: title,
        price: price,
        compare_at_price: null,
        featured_image: imageUrl,
        images: imageUrl ? [imageUrl] : [],
        variantId: variantId,
        available: true
      };
      
      this.removedProductsData.set(productId, productData);
    } catch (error) {
      // Silently handle errors
    }
  }

  checkCartItemForRestrictedTags(cartItemElement, itemIndex) {
    try {
      const productTags = cartItemElement.getAttribute('data-product-tags') || 
                         cartItemElement.getAttribute('data-tags') || '';
      
      const tagInputs = cartItemElement.querySelectorAll('input[name*="tag"], input[data-tag]');
      let allTags = productTags;
      
      tagInputs.forEach(input => {
        const tagValue = input.value || input.getAttribute('data-tag') || '';
        if (tagValue) {
          allTags += ',' + tagValue;
        }
      });
      
      const scriptTags = cartItemElement.querySelectorAll('script[type="application/json"]');
      scriptTags.forEach(script => {
        try {
          const data = JSON.parse(script.textContent);
          if (data.tags) {
            if (Array.isArray(data.tags)) {
              allTags += ',' + data.tags.join(',');
            } else if (typeof data.tags === 'string') {
              allTags += ',' + data.tags;
            }
          }
          if (data.product && data.product.tags) {
            if (Array.isArray(data.product.tags)) {
              allTags += ',' + data.product.tags.join(',');
            } else if (typeof data.product.tags === 'string') {
              allTags += ',' + data.product.tags;
            }
          }
        } catch (e) {
          // Ignore JSON parsing errors
        }
      });
      
      const titleElement = cartItemElement.querySelector('.cart__item__title a, .cart__item__title, .cart-item__title, .cart__item-name, .product-title, [data-cart-item-title]');
      if (titleElement) {
        const productTitle = titleElement.textContent.trim().toLowerCase();
        
        if (productTitle.includes('(eu)')) {
          allTags += ',eu';
        }
        if (productTitle.includes('(au)')) {
          allTags += ',au-tub';
        }
        if (productTitle.includes('(au-tub)')) {
          allTags += ',au-tub';
        }
        
        // Check for exclusive drop products
        if (productTitle.includes('premium energy - single can blind box') ||
            productTitle.includes('1 of 1 mega pallet') ||
            productTitle.includes('premium energy - variety pack')) {
          allTags += ',exclusive-drop';
        }
      }
      
      const tagsArray = allTags.toLowerCase()
        .split(/[,\s]+/)
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      const restrictedTags = ['au-tub', 'eu', 'exclusive-drop'];
      const hasRestricted = tagsArray.some(tag => restrictedTags.includes(tag));
      
      if (hasRestricted) {
        this.hasRestrictedItems = true;
        return;
      }
      
   
      
    } catch (error) {
      // Silently handle errors
    }
  }

  

  async handleRemovedItems() {
    if (this.previousCartProductIds.size === 0) {
      return;
    }

    const removedProductIds = Array.from(this.previousCartProductIds).filter(id => 
      !this.currentCartProductIds.has(id)
    );

    if (removedProductIds.length > 0) {
      let addedNewRecommendations = false;
      
      for (const productId of removedProductIds) {
        if (this.addedFromRecommendations.has(productId)) {
          this.addedFromRecommendations.delete(productId);
          continue;
        }
        
        const existingDynamicIndex = this.dynamicRecommendations.findIndex(rec => 
          rec.productId === productId.toString()
        );
        if (existingDynamicIndex !== -1) {
          continue;
        }
        
        let isOriginallyRecommended = false;
        
        if (this.originalRecommendationProductIds.has(productId)) {
          isOriginallyRecommended = true;
        }
        
        if (!isOriginallyRecommended) {
          for (const originalRec of this.originalRecommendations) {
            const form = originalRec.element.querySelector('form[data-product-form]');
            if (form) {
              const variantIdInput = form.querySelector('input[name="id"]');
              const productIdInput = form.querySelector('input[name="product-id"]');
              
              if ((variantIdInput && variantIdInput.value === productId) ||
                  (productIdInput && productIdInput.value === productId)) {
                isOriginallyRecommended = true;
                break;
              }
            }
          }
        }
        
        if (!isOriginallyRecommended) {
          await this.addRemovedItemToRecommendations(productId);
          addedNewRecommendations = true;
        }
      }
      
      if (addedNewRecommendations) {
        this.filterRecommendations();
      }
    }
  }

  async addRemovedItemToRecommendations(productId) {
    try {
      const existingIndex = this.dynamicRecommendations.findIndex(rec => rec.productId === productId.toString());
      if (existingIndex !== -1) {
        return;
      }
      
      const storedProductData = this.removedProductsData.get(productId);
      
      if (!storedProductData) {
        return await this.addRemovedItemToRecommendationsViaAPI(productId);
      }
      
      if (this.isProductRestricted(storedProductData)) {
        return;
      }
      
      if (!storedProductData.id || !storedProductData.title || storedProductData.title === 'Unknown Product') {
        return;
      }
      
      const newRecommendation = this.createDynamicRecommendation(storedProductData);
      if (newRecommendation) {
        this.dynamicRecommendations.push(newRecommendation);
      }
      
      if (this.dynamicRecommendations.length > 3) {
        this.dynamicRecommendations = this.dynamicRecommendations.slice(-3);
      }
      
    } catch (error) {
      // Silently handle errors
    }
  }

  async addRemovedItemToRecommendationsViaAPI(productId) {
    try {
      const existingIndex = this.dynamicRecommendations.findIndex(rec => rec.productId === productId.toString());
      if (existingIndex !== -1) {
        return;
      }
      
      let response = await fetch(`/variants/${productId}.js`);
      let productData;
      
      if (response.ok) {
        const variantData = await response.json();
        
        productData = {
          id: variantData.product_id || variantData.id,
          title: variantData.name || variantData.product_title || variantData.title || 'Unknown Product',
          price: variantData.price || 0,
          compare_at_price: variantData.compare_at_price || null,
          featured_image: variantData.featured_image || variantData.image || variantData.product_image || null,
          images: variantData.product_images || variantData.images || (variantData.featured_image ? [variantData.featured_image] : []),
          variants: [variantData],
          available: variantData.available !== false
        };
        
        try {
          const searchResponse = await fetch(`/search/suggest.json?q=${encodeURIComponent(variantData.name)}&resources[type]=product&resources[limit]=1`);
          if (searchResponse.ok) {
            const searchData = await searchResponse.json();
            
            if (searchData.resources && searchData.resources.results && searchData.resources.results.products && searchData.resources.results.products.length > 0) {
              const productInfo = searchData.resources.results.products[0];
              
              productData.id = productInfo.id || productData.id;
              productData.title = productInfo.title || productData.title;
              productData.featured_image = productInfo.featured_image || productData.featured_image;
              productData.images = productInfo.images || productData.images;
              productData.tags = productInfo.tags || productData.tags;
            }
          }
        } catch (searchError) {
          // Silently handle search errors
        }
      } else {
        response = await fetch(`/products/${productId}.js`);
        if (!response.ok) {
          return;
        }
        productData = await response.json();
      }
      
      if (this.isProductRestricted(productData)) {
        return;
      }
      
      if (!productData.id || !productData.title || productData.title === 'Unknown Product') {
        return;
      }
      
      const finalExistingIndex = this.dynamicRecommendations.findIndex(rec => rec.productId === productData.id.toString());
      if (finalExistingIndex !== -1) {
        return;
      }
      
      const newRecommendation = this.createDynamicRecommendation(productData);
      if (newRecommendation) {
        this.dynamicRecommendations.push(newRecommendation);
      }
      
      if (this.dynamicRecommendations.length > 3) {
        this.dynamicRecommendations = this.dynamicRecommendations.slice(-3);
      }
      
    } catch (error) {
      // Silently handle errors
    }
  }

  createDynamicRecommendation(productData) {
    const variants = productData.variants || [];
    const hasMultipleVariants = variants.length > 1;
    let imageUrl = '';
    
    try {
      if (productData.featured_image) {
        if (typeof productData.featured_image === 'string') {
          imageUrl = productData.featured_image;
        } else if (productData.featured_image.src) {
          imageUrl = productData.featured_image.src;
        }
      } else if (productData.images && productData.images.length > 0) {
        const firstImage = productData.images[0];
        if (typeof firstImage === 'string') {
          imageUrl = firstImage;
        } else if (firstImage && firstImage.src) {
          imageUrl = firstImage.src;
        }
      }
      
      if (imageUrl && !imageUrl.includes('data:image')) {
        if (!imageUrl.includes('?') && !imageUrl.includes('_120x120')) {
          imageUrl = imageUrl.includes('//') ? `${imageUrl}?v=120x120&crop=center` : imageUrl;
        }
      } else {
        imageUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNmNWY1ZjUiLz48L3N2Zz4=';
      }
    } catch (imageError) {
      imageUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNmNWY1ZjUiLz48L3N2Zz4=';
    }
    
    const defaultVariant = variants[0] || {};
    const variantId = defaultVariant.id || productData.variantId || productData.id;
    
    if (!variantId) {
      return null;
    }

    // Ensure we have valid price data
    let defaultPrice = defaultVariant.price || productData.price || 0;
    let defaultComparePrice = defaultVariant.compare_at_price || productData.compare_at_price || null;
    
    // Convert prices to cents if they appear to be in dollars
    if (defaultPrice > 0 && defaultPrice < 100 && defaultPrice % 1 !== 0) {
      defaultPrice = Math.round(defaultPrice * 100);
    }
    if (defaultComparePrice && defaultComparePrice > 0 && defaultComparePrice < 100 && defaultComparePrice % 1 !== 0) {
      defaultComparePrice = Math.round(defaultComparePrice * 100);
    }

    const variantOptions = variants.filter(v => v.available).map(v => {
      let vPrice = v.price || defaultPrice;
      let vComparePrice = v.compare_at_price || null;
      
      // Convert to cents if needed
      if (vPrice > 0 && vPrice < 100 && vPrice % 1 !== 0) {
        vPrice = Math.round(vPrice * 100);
      }
      if (vComparePrice && vComparePrice > 0 && vComparePrice < 100 && vComparePrice % 1 !== 0) {
        vComparePrice = Math.round(vComparePrice * 100);
      }
      
      return {
        id: v.id,
        title: v.title,
        price: vPrice,
        compare_at_price: vComparePrice,
        available: v.available
      };
    });
    
    const uniqueId = `dyn-${productData.id}-${Date.now()}`;
    
    const recommendationHtml = `
      <div class="cart-recommendation-product cart-recommendation-product--dynamic" data-product-id="${productData.id}">
        <div class="cart-recommendation-product__top">
          <div class="cart-recommendation-product__image">
            <img src="${imageUrl}" alt="${productData.title.replace(/"/g, '&quot;')}" width="120" height="120" onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNmNWY1ZjUiLz48L3N2Zz4=';">
            ${(defaultComparePrice && defaultComparePrice > defaultPrice) ? '<span class="cart-recommendation-product__badge">Sale</span>' : ''}
          </div>
          
                      <div class="cart-recommendation-product__info">
              <h4 class="cart-recommendation-product__title">${productData.title.replace(/"/g, '&quot;')}</h4>
              
              <div class="cart-recommendation-product__removed-note">Recently removed</div>
            </div>
          
          <form method="post" action="/cart/add" id="${uniqueId}" class="quick__form" data-product-form data-quickbuy-form>
            ${!hasMultipleVariants ? `<input type="hidden" name="id" value="${variantId}">` : ''}
            
            <input type="hidden" name="quantity" value="1">
            <input type="hidden" name="product-id" value="${productData.id}">
            
            <button type="submit" 
              name="add"
              class="cart-recommendation-product__add-btn"
              data-add-to-cart
              data-atc-trigger
              data-upsell-add
              aria-label="Add ${productData.title.replace(/"/g, '&quot;')} to cart">
              add
            </button>
            
            <div class="product-grid-item__error" role="alert" data-cart-errors-container></div>
          </form>
        </div>
        
        ${hasMultipleVariants ? `
          <div class="cart-recommendation-product__variants">
            <select name="id" 
              class="cart-recommendation-product__variant-select" 
              data-variant-select
              aria-label="Select ${productData.title.replace(/"/g, '&quot;')} variant"
              form="${uniqueId}">
              ${variantOptions.map(v => `
                <option value="${v.id}" 
                  data-price="${v.price}"
                  data-compare-price="${v.compare_at_price || ''}"
                  ${v.id === variantId ? 'selected' : ''}>
                  ${v.title}
                </option>
              `).join('')}
            </select>
          </div>
        ` : ''}
      </div>
    `;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = recommendationHtml;
    const element = tempDiv.firstElementChild;
    
    // No price change handler needed since we don't show prices for recently removed items
    
    return {
      element: element,
      productId: productData.id.toString(),
      isDynamic: true,
      title: productData.title
    };
  }

  formatMoney(cents) {
    // Handle various input types and ensure we have a valid number
    let amount = cents;
    
    // If it's already a decimal (dollars), convert to cents
    if (typeof amount === 'string') {
      amount = parseFloat(amount);
    }
    
    // If amount is NaN, null, undefined, or not a number, default to 0
    if (isNaN(amount) || amount === null || amount === undefined) {
      amount = 0;
    }
    
    // If the amount seems to be in dollars (less than 100), convert to cents
    // This handles cases where the API returns prices in dollars instead of cents
    if (amount > 0 && amount < 100 && amount % 1 !== 0) {
      amount = amount * 100;
    }
    
    const dollars = (amount / 100).toFixed(2);
    return `$${dollars}`;
  }

  filterRecommendations() {
    if (!this.cartRecommendationsContainer || this.originalRecommendations.length === 0) {
      return;
    }

    if (this.hasRestrictedItems) {
      this.cartRecommendationsContainer.style.display = 'none';
      return;
    }
  
    const productsContainer = this.cartRecommendationsContainer.querySelector('.cart-recommendations__products');
    if (!productsContainer) {
      return;
    }
  
    productsContainer.innerHTML = '';
  
    const dynamicRecommendationProductIds = new Set();
    this.dynamicRecommendations.forEach(rec => {
      if (rec.productId) {
        dynamicRecommendationProductIds.add(rec.productId.toString());
      }
    });
    
    const availableOriginalRecommendations = [];
    const availableDynamicRecommendations = [];
    const minRecommendations = 4;
    const maxTotalProducts = 5;
    
    this.originalRecommendations.forEach((recommendation, index) => {
      const productId = recommendation.productId;
      const isInCart = this.isProductInCart(productId, recommendation.element);
      const hasDynamicVersion = dynamicRecommendationProductIds.has(productId.toString());
      
      if (!isInCart && !hasDynamicVersion) {
        availableOriginalRecommendations.push(recommendation);
      }
    });
    
    this.dynamicRecommendations.forEach((recommendation, index) => {
      const productId = recommendation.productId;
      const isInCart = this.isProductInCart(productId, recommendation.element);
      
      if (!isInCart) {
        availableDynamicRecommendations.push(recommendation);
      }
    });
  
    let addedCount = 0;
    const totalAvailable = availableOriginalRecommendations.length + availableDynamicRecommendations.length;
    
    if (totalAvailable < minRecommendations) {
      this.originalRecommendations.forEach((recommendation) => {
        if (addedCount >= minRecommendations) return;
        
        const productId = recommendation.productId;
        const isCurrentlyInCart = this.isProductInCart(productId, recommendation.element);
        const hasDynamicVersion = dynamicRecommendationProductIds.has(productId.toString());
        
        if (!isCurrentlyInCart && !hasDynamicVersion && !availableOriginalRecommendations.some(r => r.productId === productId)) {
          availableOriginalRecommendations.push(recommendation);
        }
      });
    }
    
    availableDynamicRecommendations.forEach((recommendation, index) => {
      if (addedCount >= maxTotalProducts) return;
      
      const clonedElement = recommendation.element.cloneNode(true);
      clonedElement.style.animationDelay = `${0.3 + (addedCount * 0.1)}s`;
      productsContainer.appendChild(clonedElement);
      addedCount++;
    });
    
    const remainingNeeded = Math.max(minRecommendations - addedCount, 0);
    const totalToAdd = Math.min(availableOriginalRecommendations.length, maxTotalProducts - addedCount, Math.max(remainingNeeded, maxTotalProducts - addedCount));
    
    availableOriginalRecommendations.slice(0, totalToAdd).forEach((recommendation, index) => {
      const clonedElement = recommendation.element.cloneNode(true);
      clonedElement.style.animationDelay = `${0.3 + (addedCount * 0.1)}s`;
      productsContainer.appendChild(clonedElement);
      addedCount++;
    });
    
    if (addedCount === 0) {
      this.cartRecommendationsContainer.style.display = 'none';
    } else {
      this.cartRecommendationsContainer.style.display = '';
    }
  }

  isProductInCart(productId, recommendationElement) {
    // Normalize productId to string for consistent comparison
    const normalizedProductId = productId ? productId.toString() : '';
    
    if (!normalizedProductId) {
      return false;
    }
    
    // Simple check: is this product ID in our cart?
    const isInCart = this.currentCartProductIds.has(normalizedProductId);
    
    return isInCart;
  }

  setupCartObserver() {
    if (!this.cartItemsContainer) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      let cartChanged = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const target = mutation.target;
          
          if (target === this.cartItemsContainer || 
              target.closest('#cartItemsHolder, .cart-drawer__items, .cart__items')) {
            cartChanged = true;
          }
        }
        
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'data-item' || mutation.attributeName === 'data-cart-item')) {
          cartChanged = true;
        }
      });
      
      if (cartChanged) {
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 100);
      }
    });

    observer.observe(this.cartItemsContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-item', 'data-cart-item']
    });
  }

  setupCartEventListeners() {
    const cartEvents = ['cart:updated', 'cart:item-added', 'cart:item-removed', 'cart:quantity-changed', 'cart:open'];
    cartEvents.forEach(eventName => {
      document.addEventListener(eventName, () => {
        if (this.isCartDrawerOpen && this.isInitialized) {
          setTimeout(async () => {
            await this.updateCurrentCartProductIds();
            this.filterRecommendations();
          }, 100);
        }
      });
    });

    // Listen for form submissions from recommendation products
    document.addEventListener('submit', (e) => {
      if (!this.isCartDrawerOpen || !this.isInitialized) return;
      
      const form = e.target;
      if (form.closest('.cart-recommendations') && form.matches('[data-product-form], [data-quickbuy-form]')) {
        
        const productIdInput = form.querySelector('input[name="product-id"]');
        const variantIdInput = form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]');
        
        if (productIdInput && productIdInput.value) {
          this.addedFromRecommendations.add(productIdInput.value);
        }
        if (variantIdInput) {
          const variantId = variantIdInput.value || variantIdInput.options?.[variantIdInput.selectedIndex]?.value;
          if (variantId) {
            this.addedFromRecommendations.add(variantId);
          }
        }
        
        // Force update after a reasonable delay to allow cart to update
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 1000);
      }
    });

    document.addEventListener('click', (e) => {
      if (!this.isCartDrawerOpen || !this.isInitialized) return;
      
      if (e.target.matches('[data-quantity-button], [data-item-remove], [data-quantity-minus], [data-quantity-plus]')) {
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 200);
      }
      
      if (e.target.closest('.cart-recommendations') && 
          e.target.matches('[data-add-to-cart], button[type="submit"]')) {
        const form = e.target.closest('form');
        if (form) {
          const productIdInput = form.querySelector('input[name="product-id"]');
          const variantIdInput = form.querySelector('input[name="id"]') || form.querySelector('select[name="id"]');
          
          if (productIdInput && productIdInput.value) {
            this.addedFromRecommendations.add(productIdInput.value);
          }
          if (variantIdInput) {
            const variantId = variantIdInput.value || variantIdInput.options?.[variantIdInput.selectedIndex]?.value;
            if (variantId) {
              this.addedFromRecommendations.add(variantId);
            }
          }
        }
        
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 800);
      }
    });

    document.addEventListener('change', (e) => {
      if (!this.isCartDrawerOpen || !this.isInitialized) return;
      
      if (e.target.matches('[data-quantity-field]')) {
        setTimeout(async () => {
          await this.updateCurrentCartProductIds();
          this.filterRecommendations();
        }, 200);
      }
    });

    // Additional listener for Shopify's cart.js events (if using cart.js)
    if (window.CartJS) {
      CartJS.on('cart.requestComplete', () => {
        if (this.isCartDrawerOpen && this.isInitialized) {
          setTimeout(async () => {
            await this.updateCurrentCartProductIds();
            this.filterRecommendations();
          }, 100);
        }
      });
    }
  }

  async refresh() {
    if (this.isInitialized && this.isCartDrawerOpen) {
      await this.updateCurrentCartProductIds();
      this.filterRecommendations();
    }
  }

  forceInitialize() {
    this.isInitialized = false;
    this.onCartDrawerOpen();
  }

  async fetchAdditionalRecommendations(currentCount, targetCount, container) {
    try {
      const needed = targetCount - currentCount;
      
      const cartItems = Array.from(this.currentCartProductIds);
      if (cartItems.length === 0) {
        return;
      }
      
      let productIdForRecommendations = null;
      for (const [storedProductId, productData] of this.removedProductsData.entries()) {
        productIdForRecommendations = storedProductId;
        break;
      }
      
      if (!productIdForRecommendations) {
        productIdForRecommendations = cartItems[0];
      }
      
      const response = await fetch(`/recommendations/products.json?product_id=${productIdForRecommendations}&limit=${needed + 4}`);
      if (!response.ok) {
        return;
      }
      
      const data = await response.json();
      
      if (!data.products || data.products.length === 0) {
        return;
      }
      
      let addedFromAPI = 0;
      const existingProductIds = new Set();
      
      container.querySelectorAll('[data-product-id]').forEach(element => {
        const productId = element.getAttribute('data-product-id');
        if (productId) existingProductIds.add(productId);
      });
      
      this.dynamicRecommendations.forEach(rec => {
        if (rec.productId) {
          existingProductIds.add(rec.productId.toString());
        }
      });
      
      this.originalRecommendationProductIds.forEach(productId => {
        existingProductIds.add(productId.toString());
      });
      
      for (const product of data.products) {
        if (addedFromAPI >= needed) break;
        
        if (this.isProductInCart(product.id.toString(), null) || 
            existingProductIds.has(product.id.toString())) {
          continue;
        }
        
        const recommendationElement = this.createAPIRecommendation(product);
        if (recommendationElement) {
          recommendationElement.style.animationDelay = `${0.3 + ((currentCount + addedFromAPI) * 0.1)}s`;
          container.appendChild(recommendationElement);
          addedFromAPI++;
          existingProductIds.add(product.id.toString());
        }
      }
      
    } catch (error) {
      // Silently handle errors
    }
  }

  createAPIRecommendation(product) {
    try {
      const variants = product.variants || [];
      const hasMultipleVariants = variants.length > 1;
      const availableVariants = variants.filter(v => v.available);
      
      if (availableVariants.length === 0) {
        return null;
      }
      
      const defaultVariant = availableVariants[0];
      
      let imageUrl = '';
      if (product.featured_image) {
        imageUrl = product.featured_image.includes('//') ? 
          `${product.featured_image}?v=120x120&crop=center` : 
          product.featured_image;
      } else {
        imageUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjEyMCIgdmlld0JveD0iMCAwIDEyMCAxMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNmNWY1ZjUiLz48L3N2Zz4=';
      }
      
      const uniqueId = `api-${product.id}-${Date.now()}`;
      
      const recommendationHtml = `
        <div class="cart-recommendation-product cart-recommendation-product--api" data-product-id="${product.id}">
          <div class="cart-recommendation-product__top">
            <div class="cart-recommendation-product__image">
              <img src="${imageUrl}" alt="${product.title.replace(/"/g, '&quot;')}" width="120" height="120">
              ${(defaultVariant.compare_at_price && defaultVariant.compare_at_price > defaultVariant.price) ? '<span class="cart-recommendation-product__badge">Sale</span>' : ''}
            </div>
            
            <div class="cart-recommendation-product__info">
              <h4 class="cart-recommendation-product__title">${product.title.replace(/"/g, '&quot;')}</h4>
              
              <div class="cart-recommendation-product__price" data-price-wrapper>
                ${(defaultVariant.compare_at_price && defaultVariant.compare_at_price > defaultVariant.price) ? 
                  `<span class="cart-recommendation-product__price--sale">${this.formatMoney(defaultVariant.price)}</span>
                   <span class="cart-recommendation-product__price--compare">${this.formatMoney(defaultVariant.compare_at_price)}</span>` :
                  this.formatMoney(defaultVariant.price)
                }
              </div>
            </div>
            
            <form method="post" action="/cart/add" id="${uniqueId}" class="quick__form" data-product-form data-quickbuy-form>
              ${!hasMultipleVariants ? `<input type="hidden" name="id" value="${defaultVariant.id}">` : ''}
              
              <input type="hidden" name="quantity" value="1">
              <input type="hidden" name="product-id" value="${product.id}">
              
              <button type="submit" 
                name="add"
                class="cart-recommendation-product__add-btn"
                data-add-to-cart
                data-atc-trigger
                data-upsell-add
                aria-label="Add ${product.title.replace(/"/g, '&quot;')} to cart">
                Add
              </button>
              
              <div class="product-grid-item__error" role="alert" data-cart-errors-container></div>
            </form>
          </div>
          
          ${hasMultipleVariants ? `
            <div class="cart-recommendation-product__variants">
              <select name="id" 
                class="cart-recommendation-product__variant-select" 
                data-variant-select
                aria-label="Select ${product.title.replace(/"/g, '&quot;')} variant"
                form="${uniqueId}">
                ${availableVariants.map(v => `
                  <option value="${v.id}" 
                    data-price="${v.price}"
                    data-compare-price="${v.compare_at_price || ''}"
                    ${v.id === defaultVariant.id ? 'selected' : ''}>
                    ${v.title}
                  </option>
                `).join('')}
              </select>
            </div>
          ` : ''}
        </div>
      `;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = recommendationHtml;
      const element = tempDiv.firstElementChild;
      
      // Add variant change handler
      if (hasMultipleVariants) {
        const variantSelect = element.querySelector('[data-variant-select]');
        const priceWrapper = element.querySelector('[data-price-wrapper]');
        
        variantSelect.addEventListener('change', (e) => {
          const option = e.target.options[e.target.selectedIndex];
          const price = parseFloat(option.dataset.price);
          const comparePrice = option.dataset.comparePrice ? parseFloat(option.dataset.comparePrice) : null;
          
          if (comparePrice && comparePrice > price) {
            priceWrapper.innerHTML = `
              <span class="cart-recommendation-product__price--sale">${this.formatMoney(price)}</span>
              <span class="cart-recommendation-product__price--compare">${this.formatMoney(comparePrice)}</span>
            `;
          } else {
            priceWrapper.innerHTML = this.formatMoney(price);
          }
        });
      }
      
      return element;
      
    } catch (error) {
      // Silently handle errors
      return null;
    }
  }

  isProductRestricted(productData) {
    try {
      if (productData.title) {
        const title = productData.title.toLowerCase();
        
        if (title.includes('(eu)') || title.includes('(au)') || title.includes('(au-tub)')) {
          return true;
        }
      }
      
      if (productData.tags) {
        let tags = [];
        if (Array.isArray(productData.tags)) {
          tags = productData.tags;
        } else if (typeof productData.tags === 'string') {
          tags = productData.tags.split(',').map(tag => tag.trim());
        }
        
        const restrictedTags = ['au-tub', 'eu', 'exclusive-drop'];
        const hasRestrictedTag = tags.some(tag => 
          restrictedTags.includes(tag.toLowerCase().trim())
        );
        
        if (hasRestrictedTag) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      // Silently handle errors
      return false;
    }
  }
}

window.cartRecommendations = new CartRecommendations();